import React, { useEffect, useMemo, useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from './ui/dialog';
import { DateRangePicker } from './ui/date-range-picker';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from './ui/table';
import { Building2, ChevronLeft, Clock, Loader2, Table2 } from 'lucide-react';
import { Device, DeviceStatus } from '../features/device/types/device';
import { formatDateUTC } from '../utils/unitUtils';
import { formatTimeForDisplay } from '../lib/formatUtils';
import { Pagination } from './ui/Pagination';
import {
    COATING_DATA_PAGE_SIZE_OPTIONS,
    DEFAULT_COATING_DATA_PAGE_SIZE,
    normalizeCoatingDataPageSize,
} from '../utils/coatingDataPagination';

interface JointDataModalProps {
    isOpen: boolean;
    onClose: () => void;
    edgeUid?: string;
    device?: Device;
}

type JointTimeRange = 'daterange' | '15min' | '30min' | '1hour' | '4hours';

interface JointDateRange {
    dateFrom: Date | undefined;
    dateTo: Date | undefined;
}

const statusBadgeColors: Record<string, string> = {
    Pending: 'text-readable-red bg-[rgba(196,0,31,0.06)]',
    Failed: 'text-readable-red bg-[rgba(196,0,31,0.06)]',
    CertificateAvailable: 'text-[#B38600] bg-[rgba(230,161,0,0.08)]',
    DownloadComplete: 'text-galvanizing-green bg-[rgba(31,154,58,0.08)]',
    Deregistered: 'bg-page-bg text-heritage-grey border border-[#E2E5EA]',
    AdminApprovalPending: 'text-[#B38600] bg-[rgba(230,161,0,0.08)]',
    AdminApproved: 'text-galvanizing-green bg-[rgba(31,154,58,0.08)]',
    AdminRejected: 'bg-page-bg text-heritage-grey border border-[#E2E5EA]',
    Register: 'text-blueprint-blue bg-[rgba(0,39,63,0.06)]',
    PublishReady: 'text-galvanizing-green bg-[rgba(31,154,58,0.08)]',
    connected: 'text-galvanizing-green bg-[rgba(31,154,58,0.08)]',
    disconnected: 'text-readable-red bg-[rgba(196,0,31,0.06)]',
    communicating: 'text-blueprint-blue bg-[rgba(0,39,63,0.06)]',
    online: 'text-galvanizing-green bg-[rgba(31,154,58,0.08)]',
    offline: 'text-readable-red bg-[rgba(196,0,31,0.06)]',
};

const statusNameMap: Record<string, string> = {
    Pending: 'Registration Pending',
    Failed: 'Registration Failed',
    CertificateAvailable: 'Registration Pending',
    DownloadComplete: 'Registered',
    Deregistered: 'Decommissioned',
    AdminApproval: 'Awaiting Admin Approval',
    AdminApprovalPending: 'Awaiting Admin Approval',
    AdminApproved: 'Approved',
    AdminRejected: 'Decommissioned',
    Register: 'Enable Re-registration',
    PublishReady: 'Active',
    connected: 'Connected',
    disconnected: 'Disconnected',
    communicating: 'Communicating',
    online: 'Online',
    offline: 'Offline',
};

const PREFERRED_COLUMNS = [
    'createdAt',
    'jointRef',
    'timestamp',
    'prefix',
    'Fill Time',
    'fill_time',
    'totalTime',
    'jointVolume',
    'chargedVolume',
    'purgedVolume',
    'infillTemp',
    'extruderTemp',
    'chargedTIme',
    'chargedTime',
    'injectionTIme',
    'injectionTime',
    'backPressure',
    'lockBarPressure',
    'extruderPressure',
    'infillPressureMaxReading',
    'accumulatorPressure_1',
    'accumulatorPressure_2',
    'chillRunTime',
    'backPressureRunTime',
] as const;

const HIDDEN_COLUMNS = new Set([
    'createdAt',
    'updatedAt',
    'project_id',
    'sensor_data',
    'event',
    'coating_type',
    'edge_uid',
    'id',
]);

const formatColumnLabel = (key: string): string => {
    const knownLabels: Record<string, string> = {
        createdAt: 'Created At',
        jointRef: 'Joint Ref',
        timestamp: 'Timestamp',
        prefix: 'Prefix',
        'Fill Time': 'Fill Time',
        fill_time: 'Fill Time',
        totalTime: 'Total Time',
        jointVolume: 'Joint Volume',
        chargedVolume: 'Charged Volume',
        purgedVolume: 'Purged Volume',
        infillTemp: 'Infill Temp',
        extruderTemp: 'Extruder Temp',
        chargedTIme: 'Charged Time',
        chargedTime: 'Charged Time',
        injectionTIme: 'Injection Time',
        injectionTime: 'Injection Time',
        backPressure: 'Back Pressure',
        lockBarPressure: 'Lock Bar Pressure',
        extruderPressure: 'Extruder Pressure',
        infillPressureMaxReading: 'Infill Pressure Max',
        accumulatorPressure_1: 'Accumulator Pressure 1',
        accumulatorPressure_2: 'Accumulator Pressure 2',
        chillRunTime: 'Chill Run Time',
        backPressureRunTime: 'Back Pressure Run Time',
    };

    if (knownLabels[key]) {
        return knownLabels[key];
    }

    return key
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

const DATE_TIME_COLUMNS = new Set([
    'createdAt',
    'updatedAt',
    'timestamp',
    'chargedTIme',
    'chargedTime',
    'injectionTIme',
    'injectionTime',
]);

/** Normalize ISO / Date / DD-MM-YYYY HH:mm:ss.SSS (UTC) for formatTimeForDisplay */
const normalizeTimestampForDisplay = (
    value: unknown
): string | number | undefined => {
    if (value === null || value === undefined || value === '') {
        return undefined;
    }

    if (value instanceof Date) {
        return value.toISOString();
    }

    if (typeof value === 'number') {
        return value;
    }

    if (typeof value !== 'string') {
        return undefined;
    }

    const trimmed = value.trim();
    const match = trimmed.match(
        /^(\d{2})-(\d{2})-(\d{4})\s+(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?$/
    );

    if (match) {
        const [, dd, mm, yyyy, hh, min, ss, ms = '0'] = match;
        return Date.UTC(
            Number(yyyy),
            Number(mm) - 1,
            Number(dd),
            Number(hh),
            Number(min),
            Number(ss),
            Number((ms + '000').slice(0, 3))
        );
    }

    return trimmed;
};

const looksLikeDateTimeValue = (value: unknown): boolean => {
    if (value instanceof Date) {
        return true;
    }
    if (typeof value === 'number') {
        return value > 1_000_000_000_000 || (value > 1_000_000_000 && value < 10_000_000_000);
    }
    if (typeof value !== 'string') {
        return false;
    }
    const trimmed = value.trim();
    return (
        /^\d{2}-\d{2}-\d{4}\s+\d{2}:\d{2}:\d{2}/.test(trimmed) ||
        /^\d{4}-\d{2}-\d{2}T/.test(trimmed) ||
        /^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/.test(trimmed)
    );
};

const isDateTimeColumn = (key: string, value: unknown): boolean => {
    if (DATE_TIME_COLUMNS.has(key)) {
        return true;
    }
    // Sensor JSON fields like chargedTIme / injectionTIme / any *Time* / *Date* key
    return /time|date/i.test(key) && looksLikeDateTimeValue(value);
};

const DECIMAL_2_COLUMNS = new Set([
    'jointVolume',
    'chargedVolume',
    'purgedVolume',
    'infillTemp',
    'extruderTemp',
    'lockBarPressure',
    'extruderPressure',
    'infillPressureMaxReading',
    'accumulatorPressure_1',
    'accumulatorPressure_2',
]);

const formatCellValue = (key: string, value: unknown): string => {
    if (value === null || value === undefined || value === '') {
        return '-';
    }

    if (isDateTimeColumn(key, value)) {
        return formatTimeForDisplay(normalizeTimestampForDisplay(value));
    }

    if (DECIMAL_2_COLUMNS.has(key)) {
        const num = typeof value === 'number' ? value : Number(value);
        if (!Number.isNaN(num)) {
            return num.toFixed(2);
        }
    }

    if (typeof value === 'object') {
        return JSON.stringify(value);
    }

    return String(value);
};

const getStatusBadgeColor = (status?: DeviceStatus | string) => {
    if (!status) {
        return 'bg-page-bg text-heritage-grey border border-[#E2E5EA]';
    }
    return statusBadgeColors[status] || 'bg-page-bg text-heritage-grey border border-[#E2E5EA]';
};

const getStatusName = (status?: DeviceStatus | string) => {
    if (!status) {
        return '-';
    }
    return statusNameMap[status] || status.charAt(0).toUpperCase() + status.slice(1);
};

const getStartOfToday = (): Date => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
};

const getEndOfToday = (): Date => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today;
};

export const JointDataModal: React.FC<JointDataModalProps> = ({
    isOpen,
    onClose,
    edgeUid,
    device,
}) => {
    const [joints, setJoints] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_COATING_DATA_PAGE_SIZE);
    const [totalItems, setTotalItems] = useState(0);
    const [timeRange, setTimeRange] = useState<JointTimeRange>('daterange');
    const [dateRange, setDateRange] = useState<JointDateRange>({
        dateFrom: getStartOfToday(),
        dateTo: getEndOfToday(),
    });

    const processType =
        device?.deviceData?.process_type || device?.deviceData?.processType || '-';
    const platform = device?.deviceData?.platform || '-';

    const infoItems = useMemo(() => {
        if (!device) {
            return [];
        }

        return [
            ...(device.metadata?.ipAddress ||
            device.deviceData?.ipAddress ||
            device.deviceData?.ip_address
                ? [
                      {
                          label: 'IP Address',
                          value:
                              device.metadata?.ipAddress ||
                              device.deviceData?.ipAddress ||
                              device.deviceData?.ip_address,
                      },
                  ]
                : []),
            ...(processType && processType !== '-'
                ? [
                      {
                          label: 'Process Type',
                          value:
                              typeof processType === 'string'
                                  ? processType.charAt(0).toUpperCase() + processType.slice(1)
                                  : processType,
                      },
                  ]
                : []),
            ...(platform && platform !== '-'
                ? [{ label: 'Platform', value: platform }]
                : []),
        ];
    }, [
        device,
        processType,
        platform,
    ]);

    const formatUTCTimestamp = (date: Date): string => {
        const day = date.getUTCDate().toString().padStart(2, '0');
        const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
        const year = date.getUTCFullYear();
        const hours = date.getUTCHours().toString().padStart(2, '0');
        const minutes = date.getUTCMinutes().toString().padStart(2, '0');
        const seconds = date.getUTCSeconds().toString().padStart(2, '0');
        const milliseconds = date.getUTCMilliseconds().toString().padStart(3, '0');
        return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}.${milliseconds}`;
    };

    const getTimestamps = () => {
        let startTimestamp: number;
        let endTimestamp: number;

        switch (timeRange) {
            case 'daterange': {
                if (!dateRange.dateFrom) {
                    startTimestamp = getStartOfToday().getTime();
                    endTimestamp = getEndOfToday().getTime();
                    break;
                }
                startTimestamp = dateRange.dateFrom.getTime();
                endTimestamp = dateRange.dateTo
                    ? dateRange.dateTo.getTime()
                    : getEndOfToday().getTime();
                break;
            }
            case '15min':
            case '30min':
            case '1hour':
            case '4hours':
            default: {
                startTimestamp = getStartOfToday().getTime();
                endTimestamp = getEndOfToday().getTime();
                break;
            }
        }

        return { startTimestamp, endTimestamp };
    };

    const fetchJointData = async () => {
        if (!edgeUid || !isOpen) {
            return;
        }

        if (timeRange === 'daterange' && !dateRange.dateFrom) {
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const { startTimestamp, endTimestamp } = getTimestamps();
            const apiUrl =
                process.env.REACT_APP_LIMITS_API_BASE_URL ||
                process.env.REACT_NEW_REPORT_GENERATION_API_BASE_URL ||
                'http://localhost:8000/v1';
            const endpoint = `${apiUrl}/coating-data/joint-data`;

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    edge_uid: edgeUid,
                    start_timestamp: formatUTCTimestamp(new Date(startTimestamp)),
                    end_timestamp: formatUTCTimestamp(new Date(endTimestamp)),
                    page: currentPage,
                    limit: itemsPerPage,
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const responseData = await response.json();
            const data = responseData?.data?.data || responseData?.data || responseData;
            const jointRows = Array.isArray(data?.joints)
                ? data.joints
                : Array.isArray(data)
                  ? data
                  : [];

            setJoints(jointRows);
            setTotalItems(
                typeof data?.total === 'number'
                    ? data.total
                    : jointRows.length
            );
        } catch (err: any) {
            console.error('Error fetching joint data:', err);
            setError(err.message || 'Failed to fetch joint data');
            setJoints([]);
            setTotalItems(0);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen && edgeUid) {
            fetchJointData();
        } else if (!isOpen) {
            setJoints([]);
            setError(null);
            setCurrentPage(1);
            setTotalItems(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, edgeUid, timeRange, dateRange.dateFrom, dateRange.dateTo, currentPage, itemsPerPage]);

    const columns = useMemo(() => {
        const keys = new Set<string>();
        joints.forEach((row) => {
            Object.keys(row || {}).forEach((key) => {
                if (!HIDDEN_COLUMNS.has(key)) {
                    keys.add(key);
                }
            });
        });

        const preferred = PREFERRED_COLUMNS.filter((key) => keys.has(key));
        const remaining = Array.from(keys)
            .filter((key) => !PREFERRED_COLUMNS.includes(key as (typeof PREFERRED_COLUMNS)[number]))
            .sort((a, b) => a.localeCompare(b));

        return [...preferred, ...remaining];
    }, [joints]);

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="flex h-[100dvh] max-h-none w-screen max-w-none flex-col overflow-hidden m-0 rounded-none p-0 [&>button]:hidden">
                <DialogHeader className="flex-shrink-0 border-b border-[#E2E5EA] bg-page-bg p-3 sm:p-4">
                    <DialogTitle className="sr-only">Joint Data</DialogTitle>
                    <div className="bg-white border border-[#E2E5EA] rounded-lg min-w-0">
                        <div className="px-3 py-3 sm:px-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-shrink-0 self-start inline-flex items-center gap-1.5 px-2.5 h-8 rounded-lg border border-[#E2E5EA] bg-white hover:bg-page-bg transition-colors text-[12px] font-medium text-text-grey"
                            >
                                <ChevronLeft className="h-3.5 w-3.5 text-blueprint-blue" />
                                Back
                            </button>

                            <div className="flex-1 min-w-0">
                                <div className="flex items-center flex-wrap gap-2 mb-1">
                                    <span className="text-[14px] sm:text-[15px] font-mono font-semibold text-blueprint-blue break-all">
                                        {device?.id || edgeUid || 'Joint Data'}
                                    </span>

                                    {device?.communication_status && (
                                        <span
                                            className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium ${
                                                device.communication_status === 'Online'
                                                    ? 'text-galvanizing-green bg-[rgba(31,154,58,0.08)]'
                                                    : 'text-readable-red bg-[rgba(196,0,31,0.06)]'
                                            }`}
                                        >
                                            <span className="h-1.5 w-1.5 rounded-full bg-hero-red" />
                                            {device.communication_status === 'Online'
                                                ? 'Online'
                                                : 'Offline'}
                                        </span>
                                    )}

                                    {device?.status && (
                                        <span
                                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium ${getStatusBadgeColor(device.status)}`}
                                        >
                                            {getStatusName(device.status)}
                                        </span>
                                    )}

                                    {device?.project?.name && (
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium text-blueprint-blue bg-[rgba(0,39,63,0.06)]">
                                            <Building2 className="w-3 h-3" />
                                            {device.project.name}
                                        </span>
                                    )}

                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium text-blueprint-blue bg-[rgba(0,39,63,0.06)]">
                                        Joint Data
                                    </span>
                                </div>

                                <div className="text-[11px] text-heritage-grey flex flex-wrap items-center gap-x-3 gap-y-0.5">
                                    {infoItems.map((item, idx) => (
                                        <span key={idx} className="min-w-0 break-all">
                                            <span className="font-medium">{item.label}:</span>{' '}
                                            {item.value}
                                        </span>
                                    ))}
                                    {device?.EdgeFWVersion && device.EdgeFWVersion !== '-' && (
                                        <span>
                                            <span className="font-medium">Firmware:</span> v
                                            {device.EdgeFWVersion}
                                        </span>
                                    )}
                                    {device?.lastSeen && (
                                        <span>
                                            <span className="font-medium">Last seen:</span>{' '}
                                            {formatDateUTC(device.lastSeen)}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogHeader>

                <div className="px-6 py-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <div className="flex items-center gap-2 min-w-0">
                            <span className="text-sm font-medium text-gray-700">Total Joints:</span>
                            <span className="text-[14px] sm:text-[15px] font-mono font-semibold text-blueprint-blue">
                                {totalItems}
                            </span>
                            {isLoading && (
                                <span className="text-xs text-gray-500">(refreshing...)</span>
                            )}
                        </div>
                        <div className="flex flex-wrap items-center justify-end gap-3 ml-auto">
                            <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
                                Date Range
                            </span>
                            {timeRange === 'daterange' && (
                                <div className="min-w-[200px] sm:w-auto">
                                    <DateRangePicker
                                        showCompare={false}
                                        showTime
                                        align="end"
                                        onUpdate={(values) => {
                                            setDateRange({
                                                dateFrom: values.dateFrom,
                                                dateTo: values.dateTo,
                                            });
                                            setCurrentPage(1);
                                        }}
                                        initialDateFrom={dateRange.dateFrom}
                                        initialDateTo={dateRange.dateTo}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                    {error && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg m-4">
                            <p className="text-sm text-red-700">{error}</p>
                        </div>
                    )}

                    {!error && (
                        <div className="relative flex min-h-0 flex-1 flex-col p-4 sm:p-0">
                            {isLoading && (
                                <div className="absolute inset-0 z-20 flex flex-col items-center justify-center rounded-lg bg-white/70 backdrop-blur-sm">
                                    <Loader2 className="h-10 w-10 animate-spin text-[#EB1C37]" />
                                    <p className="mt-3 text-sm font-medium text-gray-600">
                                        Loading joint data...
                                    </p>
                                </div>
                            )}

                            {!isLoading && joints.length === 0 ? (
                                <div className="flex flex-1 items-center justify-center rounded-lg border border-gray-200 bg-white">
                                    <div className="text-center text-gray-500 py-12">
                                        <Table2 className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                                        <p className="text-sm">No joint data available for this range</p>
                                    </div>
                                </div>
                            ) : joints.length === 0 ? (
                                <div className="min-h-0 flex-1 rounded-lg border border-gray-200 bg-white" />
                            ) : (
                                <div
                                    className={`min-h-0 flex-1 overflow-x-auto overflow-y-auto rounded-lg border border-gray-200 bg-white scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100 ${
                                        isLoading ? 'pointer-events-none opacity-50' : ''
                                    }`}
                                >
                                    <Table containerClassName="relative w-max min-w-full">
                                        <TableHeader className="sticky top-0 z-10 bg-page-bg">
                                            <TableRow>
                                                {columns.map((column) => (
                                                    <TableHead
                                                        key={column}
                                                        className="whitespace-nowrap min-w-[140px] text-center"
                                                    >
                                                        {formatColumnLabel(column)}
                                                    </TableHead>
                                                ))}
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {joints.map((row, index) => (
                                                <TableRow
                                                    key={row.id ?? `${row.jointRef ?? 'joint'}-${index}`}
                                                >
                                                    {columns.map((column) => (
                                                        <TableCell
                                                            key={`${index}-${column}`}
                                                            className="whitespace-nowrap text-center"
                                                        >
                                                            {formatCellValue(column, row?.[column])}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                    <Pagination
                                        currentPage={currentPage}
                                        totalItems={totalItems}
                                        itemsPerPage={itemsPerPage}
                                        onPageChange={setCurrentPage}
                                        onItemsPerPageChange={(size) => {
                                            setItemsPerPage(normalizeCoatingDataPageSize(size));
                                        }}
                                        pageSizeOptions={[...COATING_DATA_PAGE_SIZE_OPTIONS]}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <DialogFooter className="px-6 py-4 border-t border-gray-200 flex-shrink-0" />
            </DialogContent>
        </Dialog>
    );
};
